import { ReactElement, useEffect, useRef, useState } from 'react';
import { Grid } from '@mantine/core';
import { isMobile } from 'react-device-detect';
import { useJobContext } from '@company/state/jobContext';
import ModalShareApplicantLink from '@company/components/th/ThWorkerCard/ModalShareApplicantLink';
import type { IWorkerData } from '@th-types/worker.type';
import CandidatesPanel from '@company/pages/JobOverview/DetailV2/CandidatesPanel/CandidatesPanel';
import JobPanel from '@company/pages/JobOverview/DetailV2/JobPanel/JobPanel';
import { useJobDetailStore } from '@company/pages/JobOverview/DetailV2/stores/jobDetailStore';
import ModalDeleteJob from '@company/pages/JobOverview/Detail/Modal/ModalDeleteJob';
import ModalShareJobLink from '@company/pages/JobOverview/Detail/ModalShareJobLink';

export default function DetailV2(): ReactElement {
  const { job } = useJobContext();
  const shareWorkerId = useJobDetailStore((state) => state.shareWorkerId);
  const closeShareModal = useJobDetailStore((state) => state.closeShareModal);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareJobLinkModal, setShowShareJobLinkModal] = useState(false);

  const handleCloseModalShareJobLink = () => setShowShareJobLinkModal(false);
  const handleShowModalShareJobLink = () => setShowShareJobLinkModal(true);

  /**
   * Calculate available space for desktop to make panels fit viewport
   */
  const containerRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (isMobile) return undefined;
    const measure = () => {
      const top = containerRef.current?.getBoundingClientRect().top ?? 0;
      // Leave the body's bottom padding (2rem) visible below the panels
      setPanelHeight(window.innerHeight - top - 32);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  if (!job) {
    return <div />;
  }

  return (
    <>
      <div ref={containerRef}>
        <Grid
          flex={1}
          gutter={isMobile ? 0 : undefined}
          h={panelHeight}
          styles={{ inner: { height: panelHeight ? '100%' : undefined } }}
        >
          <Grid.Col span={{ base: 12, lg: 5 }} h={panelHeight}>
            <JobPanel
              job={job}
              panelHeight={panelHeight}
              onShareJob={handleShowModalShareJobLink}
              onDeleteJob={() => setShowDeleteModal(true)}
            />
          </Grid.Col>

          {/* Job candidates */}
          <Grid.Col span={{ base: 12, lg: 7 }} h={panelHeight}>
            <CandidatesPanel job={job} panelHeight={panelHeight} />
          </Grid.Col>
        </Grid>
      </div>

      <ModalDeleteJob
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        jobId={job.id}
        jobPosition={job.position}
      />
      {showShareJobLinkModal && (
        <ModalShareJobLink
          jobLink={job.jobShareLink}
          jobDescription={job.description}
          firstRowJob={job.firstRow}
          companyName={job.company.name}
          companyLogo={job.company.profileImageUrl}
          jobPosition={job.position}
          handleClose={handleCloseModalShareJobLink}
        />
      )}
      {shareWorkerId && (
        <ModalShareApplicantLink
          worker={{ id: shareWorkerId } as IWorkerData}
          showModal={!!shareWorkerId}
          setShowModal={(value) => {
            if (!value) closeShareModal();
          }}
        />
      )}
    </>
  );
}
